import { axiosInstance } from '../../config/axios';

// The catalogue rarely changes; fetch it once per page load.
let catalogueRequest = null;
export const getCatalogue = () => {
  if (!catalogueRequest) {
    catalogueRequest = axiosInstance.get('/v1/rulebook/catalogue').then((r) => r.data)
      .catch((e) => { catalogueRequest = null; throw e; });
  }
  return catalogueRequest;
};

export const getTemplates = () => axiosInstance.get('/v1/rulebook/templates').then((r) => r.data);

const base = (eventId) => `/v1/events/${eventId}/rulebook`;

export const rulebookApi = {
  get: (eventId, params) => axiosInstance.get(base(eventId), { params }).then((r) => r.data),
  create: (eventId, body) => axiosInstance.post(base(eventId), body).then((r) => r.data),
  copySources: (eventId) => axiosInstance.get(`${base(eventId)}/copy-sources`).then((r) => r.data),
  addRule: (eventId, rule) => axiosInstance.post(`${base(eventId)}/rules`, rule).then((r) => r.data),
  updateRule: (eventId, ruleId, changes) => axiosInstance.patch(`${base(eventId)}/rules/${ruleId}`, changes).then((r) => r.data),
  deleteRule: (eventId, ruleId) => axiosInstance.delete(`${base(eventId)}/rules/${ruleId}`).then((r) => r.data),
  reorder: (eventId, section, ruleIds) => axiosInstance.post(`${base(eventId)}/reorder`, { section, rule_ids: ruleIds }).then((r) => r.data),
  discardDraft: (eventId) => axiosInstance.delete(`${base(eventId)}/draft`).then((r) => r.data),
  publish: (eventId, body) => axiosInstance.post(`${base(eventId)}/publish`, body).then((r) => r.data),
  version: (eventId, number) => axiosInstance.get(`${base(eventId)}/versions/${number}`).then((r) => r.data),
  pdfUrl: (eventId, number) => `${axiosInstance.defaults.baseURL}${base(eventId)}/pdf${number ? `?version=${number}` : ''}`,
  // Standings rules: "How points work", the owner's re-apply preview, tie-breaker results
  standings: (eventId) => axiosInstance.get(`${base(eventId)}/standings`).then((r) => r.data),
  publishPreview: (eventId) => axiosInstance.post(`${base(eventId)}/publish/preview`).then((r) => r.data),
  addDecider: (eventId, body) => axiosInstance.post(`${base(eventId)}/deciders`, body).then((r) => r.data),
};

// Rules in the organiser's own words, read by the AI into cards (phase 4)
const readBase = (eventId) => `${base(eventId)}/read`;
const card = (eventId, importId, cardId) => `${readBase(eventId)}/${importId}/cards/${cardId}`;

export const readApi = {
  readText: (eventId, text, spoken) => axiosInstance.post(readBase(eventId), { text, spoken }).then((r) => r.data),
  readFile: (eventId, file, name) => {
    const form = new FormData();
    form.append('file', file, name || file.name);
    return axiosInstance.post(`${readBase(eventId)}/file`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 })
      .then((r) => r.data);
  },
  recent: (eventId) => axiosInstance.get(readBase(eventId)).then((r) => r.data),
  get: (eventId, importId) => axiosInstance.get(`${readBase(eventId)}/${importId}`).then((r) => r.data),
  editCard: (eventId, importId, cardId, changes) => axiosInstance.patch(card(eventId, importId, cardId), changes).then((r) => r.data),
  answer: (eventId, importId, cardId, body) => axiosInstance.post(`${card(eventId, importId, cardId)}/answer`, body).then((r) => r.data),
  add: (eventId, importId, cardId) => axiosInstance.post(`${card(eventId, importId, cardId)}/add`).then((r) => r.data),
  keepAsText: (eventId, importId, cardId) => axiosInstance.post(`${card(eventId, importId, cardId)}/text`).then((r) => r.data),
  retry: (eventId, importId, cardId, correction) => axiosInstance.post(`${card(eventId, importId, cardId)}/retry`, { correction }, { timeout: 120000 }).then((r) => r.data),
  remove: (eventId, importId, cardId) => axiosInstance.delete(card(eventId, importId, cardId)).then((r) => r.data),
  addAll: (eventId, importId) => axiosInstance.post(`${readBase(eventId)}/${importId}/add-all`).then((r) => r.data),
};

export const adjustmentsApi = {
  list: (eventId, fixtureId) => axiosInstance.get(`/v1/events/${eventId}/adjustments`, { params: { fixture_id: fixtureId } }).then((r) => r.data),
  add: (eventId, body) => axiosInstance.post(`/v1/events/${eventId}/adjustments`, body).then((r) => r.data),
  remove: (eventId, id) => axiosInstance.delete(`/v1/events/${eventId}/adjustments/${id}`).then((r) => r.data),
};

/** The server's message for a failed request, or a fallback. */
export const errorText = (e, fallback = 'Something went wrong. Try again.') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return fallback;
};
